import React, { useState, useEffect, useMemo } from 'react';
import {
  Search, Fuel, ChevronDown, ChevronUp, Calendar,
  User, FileText, Download, Loader2, Car,
  CheckCircle, Clock, X, TrendingUp,
  LayoutGrid, Table, FileSpreadsheet, RefreshCw
} from 'lucide-react';
import API_BASE_URL from '../config';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import megalogo from '../Image/megalogo.png';

export default function FuelHistory() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPlates, setExpandedPlates] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'card' | 'table'
  const [websiteInfo, setWebsiteInfo] = useState(null);

  const [showResetFuelModal, setShowResetFuelModal] = useState(false);
  const [fuelBatchHistory, setFuelBatchHistory] = useState([]);
  const [newFuelBatch, setNewFuelBatch] = useState({ liters: '', date: new Date().toISOString().split('T')[0], remarks: '' });
  const [savingBatch, setSavingBatch] = useState(false);


  const fetchFuelBatchHistory = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/FuelBatch`);
      if (!res.ok) return;
      const data = await res.json();
      setFuelBatchHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching fuel batch history:', err);
    }
  };

  const handleResetFuel = async () => {
    if (!newFuelBatch.liters || parseFloat(newFuelBatch.liters) <= 0) {
      alert('Please enter a valid liters amount!');
      return;
    }
    setSavingBatch(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // STEP 1: Save batch record
      const batchPayload = {
        liters: parseFloat(newFuelBatch.liters),
        batchDate: newFuelBatch.date,
        remarks: newFuelBatch.remarks || '',
        createdBy: user.userId || 0,
        createdByName: user.fullName || user.username || '',
        createdAt: new Date().toISOString()
      };
      const batchRes = await fetch(`${API_BASE_URL}api/FuelBatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batchPayload)
      });
      if (!batchRes.ok) throw new Error('Failed to save batch');

      // STEP 2: Add liters to inventory
      const addRes = await fetch(`${API_BASE_URL}api/InventoryData/add-fuel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          litersToAdd: parseFloat(newFuelBatch.liters),
          remarks: newFuelBatch.remarks || 'Fuel batch reset',
          addedBy: user.fullName || user.username || '',
        })
      });
      if (!addRes.ok) throw new Error('Batch saved but failed to update inventory!');

      const addData = await addRes.json();

      await fetchFuelBatchHistory();
      await fetchFuelRemaining();
      setNewFuelBatch({ liters: '', date: new Date().toISOString().split('T')[0], remarks: '' });

      alert(`✅ Done! Added ${parseFloat(newFuelBatch.liters).toLocaleString()} L to inventory.\nNew Total: ${addData.newTotal} L`);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSavingBatch(false);
    }
  };
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); }
    catch { return {}; }
  }, []);

  useEffect(() => {
    fetchTransactions();
    fetchWebsiteInfo();
  }, []);

  const fetchWebsiteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (!res.ok) return;
      const data = await res.json();
      const active = data.find(w => w.isActive) || data[0];
      if (active) setWebsiteInfo(active);
    } catch (err) {
      console.error('Error fetching website info:', err);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/FuelRequisition`);
      if (!res.ok) { setTransactions([]); return; }
      const data = await res.json();
      setTransactions(Array.isArray(data) ? data : data ? [data] : []);
    } catch (err) {
      console.error('Error:', err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const isApproved = (t) => t.approvedByVisor === true || t.approvedByVisor === 1;
  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // ─── Filter & Group ───
  const groupedByPlate = useMemo(() => {
    const q = searchTerm.toLowerCase();
    const filtered = transactions.filter(t => {
      const matchSearch =
        !q ||
        t.plateNumber?.toLowerCase().includes(q) ||
        t.requestorName?.toLowerCase().includes(q) ||
        t.department?.toLowerCase().includes(q) ||
        t.controlNo?.toLowerCase().includes(q);
      const matchStatus =
        statusFilter === 'all' ||
        (statusFilter === 'approved' && isApproved(t)) ||
        (statusFilter === 'pending' && !isApproved(t));
      return matchSearch && matchStatus;
    });

    const map = {};
    filtered.forEach(t => {
      const key = t.plateNumber || 'Unknown';
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return Object.fromEntries(
      Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
    );
  }, [transactions, searchTerm, statusFilter]);


  // Dagdag state
  const [fuelRemaining, setFuelRemaining] = useState(0);

  // Dagdag sa useEffect
  useEffect(() => {
    fetchTransactions();
    fetchWebsiteInfo();
    fetchFuelRemaining(); // ← dagdag
    fetchFuelBatchHistory();
  }, []);

  // Bagong function
  const fetchFuelRemaining = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/InventoryData`);
      if (!res.ok) return;
      const data = await res.json();
      const FUEL_KEYWORDS = ['fuel', 'diesel', 'gasoline', 'petrol', 'oil', 'lubricant', 'kerosene'];
      const total = data
        .filter(item =>
          FUEL_KEYWORDS.some(kw =>
            item.itemDescription?.toLowerCase().includes(kw) ||
            item.itemCode?.toLowerCase().includes(kw)
          )
        )
        .reduce((s, item) => s + (Number(item.quantity) || 0), 0);
      setFuelRemaining(total);
    } catch (err) {
      console.error('Error fetching fuel remaining:', err);
    }
  };
  const stats = useMemo(() => {
    const all = Object.values(groupedByPlate).flat();
    return {
      plates: Object.keys(groupedByPlate).length,
      totalTxn: all.length,
      totalLiters: all.filter(isApproved).reduce((s, t) =>
        s + (Number(t.actualLiters) || 0), 0),
      approved: all.filter(isApproved).length,
    };
  }, [groupedByPlate]);

  const plateSummary = (txns) => ({
    total: txns.filter(isApproved).reduce((s, t) => s + (Number(t.actualLiters) || 0), 0),  // ← approved lang
    approved: txns.filter(isApproved).length,
    lastDate: [...txns].sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate))[0]?.requestDate,
    maxOdo: Math.max(...txns.map(t => Number(t.truckOdometer) || 0)),
  });

  const togglePlate = (plate) =>
    setExpandedPlates(prev => ({ ...prev, [plate]: !prev[plate] }));

  const expandAll = () => {
    const all = {};
    Object.keys(groupedByPlate).forEach(p => (all[p] = true));
    setExpandedPlates(all);
  };
  const collapseAll = () => setExpandedPlates({});

  // ─── Export ALL transactions of a plate to Excel ───
  const exportPlateToExcel = (plate, txns) => {
    const companyName = websiteInfo?.websiteName || 'MEGASOFT HYGIENIC PRODUCTS, INC.';
    const wb = XLSX.utils.book_new();

    // ── Sheet 1: Transactions ──
    const { total, approved, lastDate, maxOdo } = plateSummary(txns);
    const wsData = [];

    wsData.push([companyName]);
    wsData.push([`Fuel Transaction History — Plate: ${plate}`]);
    wsData.push([`Generated: ${new Date().toLocaleString()}`]);
    wsData.push([]);
    wsData.push(['SUMMARY']);
    wsData.push(['Total Transactions', txns.length, '', 'Total Fuel (L)', total]);
    wsData.push(['Approved', approved, '', 'Pending', txns.length - approved]);
    wsData.push(['Max Odometer', maxOdo, '', 'Last Transaction', formatDate(lastDate)]);
    wsData.push([]);

    // Column headers
    wsData.push([
      'Control No.',
      'Date',
      'Requestor Name',
      'Department',
      'Description',
      'Level',
      'Qty Requested (L)',
      'Actual Liters (L)',
      'Truck Odometer',
      'Flow Meter Beg',
      'Flow Meter End',
      'Purpose',
      'Requested By',
      'Noted By',
      'Approved By',
      'Gas Man / Assist By',
      'Status',
    ]);

    // Data rows sorted by latest date first
    const sorted = [...txns].sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));
    sorted.forEach(t => {
      wsData.push([
        t.controlNo || '',
        formatDate(t.requestDate),
        t.requestorName || '',
        t.department || '',
        t.description || '',
        t.level || '',
        Number(t.quantityLiters) || 0,
        Number(t.actualLiters) || 0,
        Number(t.truckOdometer) || 0,
        Number(t.flowMeterBeg) || 0,
        Number(t.flowMeterEnd) || 0,
        t.purpose || '',
        t.requestedBy || '',
        t.notedBy || '',
        t.approvedBy || '',
        t.gasManAssistBy || '',
        isApproved(t) ? 'Approved' : 'Pending',
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [
      { wch: 16 }, { wch: 14 }, { wch: 22 }, { wch: 16 }, { wch: 14 },
      { wch: 10 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 },
      { wch: 14 }, { wch: 32 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
      { wch: 20 }, { wch: 12 },
    ];
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 16 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 16 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 16 } },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');

    // ── Sheet 2: Summary ──
    const requestorBreakdown = Object.entries(
      txns.reduce((acc, t) => {
        const key = t.requestorName || 'Unknown';
        if (!acc[key]) acc[key] = { count: 0, liters: 0 };
        acc[key].count += 1;
        acc[key].liters += Number(t.quantityLiters) || 0;
        return acc;
      }, {})
    );

    const summaryData = [
      [companyName],
      [`Plate Summary — ${plate}`],
      [],
      ['Plate Number', plate],
      ['Department', txns[0]?.department || 'N/A'],
      ['Total Transactions', txns.length],
      ['Total Fuel Requested (L)', total],
      ['Total Actual Liters (L)', txns.reduce((s, t) => s + (Number(t.actualLiters) || 0), 0)],
      ['Approved Transactions', approved],
      ['Pending Transactions', txns.length - approved],
      ['Max Odometer (km)', maxOdo],
      ['Last Transaction Date', formatDate(lastDate)],
      [],
      ['Requestor Breakdown'],
      ['Requestor', 'Transactions', 'Total Liters (L)'],
      ...requestorBreakdown.map(([name, { count, liters }]) => [name, count, liters]),
    ];

    const ws2 = XLSX.utils.aoa_to_sheet(summaryData);
    ws2['!cols'] = [{ wch: 28 }, { wch: 20 }, { wch: 18 }];
    ws2['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, 'Summary');

    XLSX.writeFile(wb, `FuelHistory_${plate}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ─── Export PDF per transaction ───
  const exportToPDF = (request) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 14;
    let y = 14;
    try { doc.addImage(megalogo, 'PNG', margin, y, 25, 13); } catch { }
    doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
    doc.text(websiteInfo?.websiteName || 'MEGASOFT HYGIENIC PRODUCTS, INC.', pageWidth / 2, y + 5, { align: 'center' });
    doc.setFontSize(7); doc.setFont(undefined, 'italic'); doc.setTextColor(100, 100, 100);
    doc.text(websiteInfo?.websiteSlogan || 'A MEGASOFT IN EVERY HOME', pageWidth / 2, y + 10, { align: 'center' });
    doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.3); doc.rect(pageWidth - margin - 42, y, 42, 16);
    doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.setTextColor(60, 60, 60);
    doc.text('DOCUMENT CODE: SPR-F-009', pageWidth - margin - 40, y + 4.5);
    doc.text('ISSUE NO: 1  |  REVISION NO: 0', pageWidth - margin - 40, y + 8.5);
    doc.text(`DATE: ${new Date().toLocaleDateString()}`, pageWidth - margin - 40, y + 12.5);
    y += 22;
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.4); doc.line(margin, y, pageWidth - margin, y);
    y += 7;
    doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 30, 30);
    doc.text('FUEL REQUISITION FORM', pageWidth / 2, y, { align: 'center' });
    doc.setDrawColor(30, 64, 175); doc.setLineWidth(0.8); doc.line(65, y + 2, 145, y + 2);
    y += 9;
    doc.setFillColor(247, 247, 247); doc.setDrawColor(180, 180, 180); doc.setLineWidth(0.3);
    doc.rect(margin, y, pageWidth - 2 * margin, 9, 'FD');
    doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(60, 60, 60);
    doc.text('CONTROL #:', margin + 3, y + 6);
    doc.setFontSize(9); doc.setTextColor(30, 64, 175);
    doc.text(request.controlNo || '—', margin + 27, y + 6);
    y += 13;
    const drawF = (label, value, x, fy) => {
      doc.setFontSize(6.5); doc.setFont(undefined, 'normal'); doc.setTextColor(130, 130, 130); doc.text(label, x, fy);
      doc.setFontSize(8.5); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20); doc.text(value || '—', x, fy + 5);
    };
    const lC = margin + 2; const rC = pageWidth / 2 + 5;
    drawF('Requestor Name', request.requestorName, lC, y);
    drawF('Department', request.department || 'N/A', rC, y); y += 11;
    drawF('Plate Number', request.plateNumber, lC, y);
    drawF('Date', formatDate(request.requestDate), rC, y); y += 14;
    const cW = (pageWidth - 2 * margin) / 4;
    doc.setFillColor(240, 240, 240); doc.setDrawColor(210, 210, 210); doc.setLineWidth(0.2);
    doc.rect(margin, y, pageWidth - 2 * margin, 7, 'FD');
    doc.setFontSize(6.5); doc.setFont(undefined, 'bold'); doc.setTextColor(70, 70, 70);
    ['DESCRIPTION', 'LEVEL', 'ACTUAL LITERS', 'TRUCK ODOMETER'].forEach((h, i) =>
      doc.text(h, margin + cW * i + 3, y + 5));
    y += 7;
    doc.setFillColor(252, 252, 252); doc.setDrawColor(210, 210, 210); doc.rect(margin, y, pageWidth - 2 * margin, 8, 'FD');
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20);
    [request.description, request.level, request.actualLiters, request.truckOdometer].forEach((v, i) =>
      doc.text(String(v ?? '—'), margin + cW * i + 3, y + 5.5));
    y += 10;
    doc.setFillColor(247, 247, 247); doc.setDrawColor(210, 210, 210);
    doc.rect(margin, y, pageWidth - 2 * margin, 14, 'FD');
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('Quantity of Liters Requested:', margin + 3, y + 5.5);
    doc.text('Flow Meter Beg:', margin + 3, y + 11); doc.text('Flow Meter End:', pageWidth / 2 + 3, y + 11);
    doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20); doc.setFontSize(8.5);
    doc.text(String(request.quantityLiters ?? '—'), margin + 57, y + 5.5);
    doc.text(String(request.flowMeterBeg ?? '—'), margin + 35, y + 11);
    doc.text(String(request.flowMeterEnd ?? '—'), pageWidth / 2 + 32, y + 11);
    y += 18;
    if (request.purpose) {
      const lines = doc.splitTextToSize(request.purpose, pageWidth - 2 * margin - 20);
      const h = Math.max(12, lines.length * 5 + 8);
      doc.setFillColor(247, 247, 247); doc.setDrawColor(210, 210, 210); doc.rect(margin, y, pageWidth - 2 * margin, h, 'FD');
      doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80); doc.text('PURPOSE:', margin + 3, y + 5.5);
      doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20); doc.setFontSize(7.5); doc.text(lines, margin + 22, y + 5.5);
      y += h + 6;
    }
    const sW = (pageWidth - 2 * margin - 5) / 2; const sH = 22;
    const drawSig = (x, sy, label, name) => {
      doc.setFillColor(255, 255, 255); doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.25);
      doc.rect(x, sy, sW, sH, 'FD');
      doc.setFontSize(6.5); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80); doc.text(label, x + 3, sy + 5);
      doc.setDrawColor(100, 100, 100); doc.setLineWidth(0.35); doc.line(x + 3, sy + 16, x + sW - 3, sy + 16);
      if (name) { doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20); doc.text(name, x + 3, sy + 14); }
      doc.setFontSize(5.5); doc.setFont(undefined, 'italic'); doc.setTextColor(140, 140, 140); doc.text('Signature Over Printed Name', x + 3, sy + 20);
    };
    drawSig(margin, y, 'REQUESTED BY', request.requestedBy || request.requestorName);
    drawSig(margin + sW + 5, y, 'NOTED BY', request.notedBy); y += sH + 4;
    drawSig(margin, y, 'APPROVED BY', request.approvedBy);
    drawSig(margin + sW + 5, y, 'GAS MAN / ASSIST BY', request.gasManAssistBy);
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3); doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
    doc.setFontSize(6); doc.setFont(undefined, 'italic'); doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    doc.text(`Control No: ${request.controlNo}  |  SPR-F-009`, pageWidth / 2, pageHeight - 4, { align: 'center' });
    doc.save(`Fuel_Requisition_${request.controlNo}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const sortedTxns = (txns) =>
    [...txns].sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">

        {/* ─── Header ─── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-4 rounded-xl shadow-lg shadow-blue-200">
                <Car className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Fuel History</h1>
                <p className="text-gray-500 mt-1">All transactions grouped by plate number</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Card / Table toggle */}
              <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-1">
                <button
                  onClick={() => setViewMode('card')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'card'
                    ? 'bg-white text-blue-700 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <LayoutGrid className="w-4 h-4" /> Card
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'table'
                    ? 'bg-white text-blue-700 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <Table className="w-4 h-4" /> Table
                </button>
              </div>
              <button
                onClick={expandAll}
                className="px-4 py-2 text-sm font-semibold bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-xl transition-all"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-4 py-2 text-sm font-semibold bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-xl transition-all"
              >
                Collapse All
              </button>
              <button
                onClick={() => setShowResetFuelModal(true)}
                className="px-4 py-2 text-sm font-semibold bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-xl transition-all flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reset Fuel
              </button>
            </div>
          </div>

          {/* Search + Status Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search plate #, requestor, department, control no..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {['all', 'approved', 'pending'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-4 py-3 rounded-xl text-sm font-semibold border transition-all capitalize ${statusFilter === s
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                    }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ─── Stats ─── */}
        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Plates', value: stats.plates, icon: Car, color: 'blue' },
              { label: 'Transactions', value: stats.totalTxn, icon: FileText, color: 'indigo' },
              { label: 'Total Fuel (Approved)', value: `${stats.totalLiters.toLocaleString()} L`, icon: Fuel, color: 'cyan' },
              { label: 'Approved', value: stats.approved, icon: CheckCircle, color: 'green' },
              { label: 'Fuel Remaining', value: `${fuelRemaining.toLocaleString()} L`, icon: Fuel, color: 'orange' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <div className={`w-9 h-9 rounded-lg bg-${color}-100 flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 text-${color}-600`} />
                </div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* ─── Content ─── */}
        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center py-24">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Loading fuel history...</p>
            </div>
          </div>
        ) : Object.keys(groupedByPlate).length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center py-24">
            <div className="text-center">
              <Car className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-400 font-medium text-lg">No records found</p>
              {(searchTerm || statusFilter !== 'all') && (
                <button
                  onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                  className="mt-3 text-sm text-blue-600 hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedByPlate).map(([plate, txns]) => {
              const isOpen = !!expandedPlates[plate];
              const { total, approved, lastDate, maxOdo } = plateSummary(txns);
              const pending = txns.length - approved;

              return (
                <div
                  key={plate}
                  className={`bg-white rounded-2xl shadow-sm border-2 transition-all duration-200 overflow-hidden ${isOpen ? 'border-blue-400 shadow-blue-100 shadow-md' : 'border-gray-100 hover:border-blue-200'
                    }`}
                >
                  {/* ─── Plate Header ─── */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-5">
                    {/* Clickable part */}
                    <button
                      onClick={() => togglePlate(plate)}
                      className="flex flex-1 items-center gap-4 text-left"
                    >
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="bg-blue-600 p-2.5 rounded-xl">
                          <Car className="w-5 h-5 text-white" />
                        </div>
                        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl px-4 py-2">
                          <p className="text-xs font-semibold text-blue-500 uppercase tracking-wider mb-0.5">Plate No.</p>
                          <p className="text-lg font-bold text-blue-800 font-mono tracking-widest">{plate}</p>
                        </div>
                      </div>

                      <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Transactions</p>
                          <p className="text-base font-bold text-gray-900 mt-0.5">{txns.length}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Total Fuel</p>
                          <p className="text-base font-bold text-blue-700 mt-0.5">{total.toLocaleString()} L</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Last Transaction</p>
                          <p className="text-sm font-semibold text-gray-700 mt-0.5">{formatDate(lastDate)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Department</p>
                          <p className="text-sm font-semibold text-gray-700 mt-0.5">{txns[0]?.department || 'N/A'}</p>
                        </div>
                      </div>
                    </button>

                    {/* Right: pills + Excel export + chevron */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {approved > 0 && (
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 border border-green-200 px-2.5 py-1 rounded-lg text-xs font-bold">
                          <CheckCircle className="w-3 h-3" /> {approved}
                        </span>
                      )}
                      {pending > 0 && (
                        <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 border border-yellow-200 px-2.5 py-1 rounded-lg text-xs font-bold">
                          <Clock className="w-3 h-3" /> {pending}
                        </span>
                      )}

                      {/* ✅ Export all transactions of this plate to Excel */}
                      <button
                        onClick={(e) => { e.stopPropagation(); exportPlateToExcel(plate, txns); }}
                        title={`Export all ${txns.length} transactions of ${plate} to Excel`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm whitespace-nowrap"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5" />
                        Export Excel
                      </button>

                      <button
                        onClick={() => togglePlate(plate)}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isOpen ? 'bg-blue-100' : 'bg-gray-100'}`}
                      >
                        {isOpen
                          ? <ChevronUp className="w-4 h-4 text-blue-600" />
                          : <ChevronDown className="w-4 h-4 text-gray-500" />
                        }
                      </button>
                    </div>
                  </div>

                  {/* ─── Expanded: Summary bar + Card/Table ─── */}
                  {isOpen && (
                    <div className="border-t border-gray-100">

                      {/* Summary bar */}
                      <div className="bg-blue-50 px-5 py-3 flex flex-wrap gap-5 border-b border-blue-100">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-blue-500" />
                          <span className="text-xs font-semibold text-blue-600">Max Odometer:</span>
                          <span className="text-xs font-bold text-blue-800">{maxOdo.toLocaleString()} km</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Fuel className="w-4 h-4 text-blue-500" />
                          <span className="text-xs font-semibold text-blue-600">Total Dispensed:</span>
                          <span className="text-xs font-bold text-blue-800">{total.toLocaleString()} Liters</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-xs font-semibold text-blue-600">Approved / Total:</span>
                          <span className="text-xs font-bold text-blue-800">{approved} / {txns.length}</span>
                        </div>
                      </div>

                      {/* ═══ CARD VIEW ═══ */}
                      {viewMode === 'card' && (
                        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {sortedTxns(txns).map((txn) => {
                            const appr = isApproved(txn);
                            return (
                              <div
                                key={txn.id}
                                className={`rounded-xl border-2 p-4 flex flex-col gap-3 transition-all hover:shadow-md ${appr ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-white'
                                  }`}
                              >
                                {/* Card header */}
                                <div className="flex items-start justify-between gap-2">
                                  <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 border border-blue-200 px-2.5 py-1 rounded-lg text-xs font-bold">
                                    <FileText className="w-3 h-3" />
                                    {txn.controlNo}
                                  </span>
                                  {appr ? (
                                    <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 border border-green-200 px-2 py-1 rounded-lg text-xs font-bold">
                                      <CheckCircle className="w-3 h-3" /> Approved
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 border border-yellow-200 px-2 py-1 rounded-lg text-xs font-bold">
                                      <Clock className="w-3 h-3" /> Pending
                                    </span>
                                  )}
                                </div>

                                {/* Card info */}
                                <div className="space-y-1.5">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-700">{formatDate(txn.requestDate)}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <User className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                                    <span className="text-sm text-gray-700">{txn.requestorName}</span>
                                  </div>
                                  {txn.purpose && (
                                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{txn.purpose}</p>
                                  )}
                                </div>

                                {/* Fuel grid */}
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 text-center">
                                    <p className="text-xs text-blue-500 font-semibold">Qty Requested</p>
                                    <p className="text-base font-bold text-blue-800">{txn.quantityLiters ?? 0} L</p>
                                  </div>
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-center">
                                    <p className="text-xs text-gray-500 font-semibold">Actual</p>
                                    <p className="text-base font-bold text-gray-800">{txn.actualLiters ?? 0} L</p>
                                  </div>
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-center">
                                    <p className="text-xs text-gray-500 font-semibold">Odometer</p>
                                    <p className="text-sm font-bold text-gray-800 font-mono">{Number(txn.truckOdometer || 0).toLocaleString()}</p>
                                  </div>
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 text-center">
                                    <p className="text-xs text-gray-500 font-semibold">Level</p>
                                    <p className="text-sm font-bold text-gray-800">{txn.level || '—'}</p>
                                  </div>
                                </div>

                                {/* Flow meter */}
                                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                                  <span className="font-semibold">Flow Meter:</span>
                                  <span>Beg <span className="font-bold text-gray-700">{txn.flowMeterBeg ?? 0}</span></span>
                                  <span>→</span>
                                  <span>End <span className="font-bold text-gray-700">{txn.flowMeterEnd ?? 0}</span></span>
                                </div>

                                {/* Approved by */}
                                {appr && (
                                  <div className="text-xs text-green-700 font-semibold bg-green-50 border border-green-100 rounded-lg px-3 py-1.5">
                                    ✓ Approved by: {txn.approvedBy}
                                  </div>
                                )}

                                {/* PDF export */}
                                <button
                                  onClick={() => exportToPDF(txn)}
                                  className="w-full flex items-center justify-center gap-1.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-all"
                                >
                                  <Download className="w-3.5 h-3.5" /> Export PDF
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* ═══ TABLE VIEW ═══ */}
                      {viewMode === 'table' && (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[1050px]">
                            <thead className="bg-gradient-to-r from-blue-700 to-blue-800 text-white">
                              <tr>
                                <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Control No.</th>
                                <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                                <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Requestor</th>
                                <th className="px-5 py-3.5 text-left text-xs font-bold uppercase tracking-wider">Description</th>
                                <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-wider">Level</th>
                                <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-wider">Qty (L)</th>
                                <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-wider">Actual (L)</th>
                                <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-wider">Odometer</th>
                                <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-wider">Status</th>
                                <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-wider">Approved By</th>
                                <th className="px-5 py-3.5 text-center text-xs font-bold uppercase tracking-wider">PDF</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {sortedTxns(txns).map((txn, idx) => {
                                const appr = isApproved(txn);
                                return (
                                  <tr
                                    key={txn.id}
                                    className={`hover:bg-blue-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                                  >
                                    <td className="px-5 py-3.5">
                                      <span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 border border-blue-200 px-2.5 py-1 rounded-lg text-xs font-bold">
                                        <FileText className="w-3 h-3" />{txn.controlNo}
                                      </span>
                                    </td>
                                    <td className="px-5 py-3.5">
                                      <div className="flex items-center gap-1.5">
                                        <Calendar className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                                        <span className="text-sm text-gray-800 font-medium">{formatDate(txn.requestDate)}</span>
                                      </div>
                                    </td>
                                    <td className="px-5 py-3.5">
                                      <div className="flex items-center gap-1.5">
                                        <User className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                                        <span className="text-sm text-gray-800 font-medium">{txn.requestorName}</span>
                                      </div>
                                    </td>
                                    <td className="px-5 py-3.5 text-sm text-gray-700">{txn.description || 'N/A'}</td>
                                    <td className="px-5 py-3.5 text-center text-sm font-semibold text-gray-700">{txn.level || '—'}</td>
                                    <td className="px-5 py-3.5 text-center">
                                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg text-xs font-bold">
                                        <Fuel className="w-3 h-3" />{txn.quantityLiters ?? 0} L
                                      </span>
                                    </td>
                                    <td className="px-5 py-3.5 text-center text-sm font-semibold text-gray-700">{txn.actualLiters ?? 0} L</td>
                                    <td className="px-5 py-3.5 text-center text-sm font-mono text-gray-700">
                                      {Number(txn.truckOdometer || 0).toLocaleString()}
                                    </td>
                                    <td className="px-5 py-3.5 text-center">
                                      {appr ? (
                                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 border border-green-200 px-2.5 py-1 rounded-lg text-xs font-bold">
                                          <CheckCircle className="w-3 h-3" /> Approved
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-700 border border-yellow-200 px-2.5 py-1 rounded-lg text-xs font-bold">
                                          <Clock className="w-3 h-3" /> Pending
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-5 py-3.5 text-center text-sm text-gray-600">{txn.approvedBy || '—'}</td>
                                    <td className="px-5 py-3.5 text-center">
                                      <button
                                        onClick={() => exportToPDF(txn)}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition-all shadow-sm"
                                      >
                                        <Download className="w-3.5 h-3.5" /> PDF
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Footer */}
                      <div className="bg-gray-50 border-t border-gray-100 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
                        <p className="text-xs text-gray-500">
                          Showing <span className="font-bold text-gray-700">{txns.length}</span> transaction{txns.length !== 1 ? 's' : ''} for plate{' '}
                          <span className="font-bold text-gray-700 font-mono">{plate}</span> — sorted by latest first
                        </p>
                        <button
                          onClick={() => exportPlateToExcel(plate, txns)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold transition-all"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5" /> Export All to Excel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {showResetFuelModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-xl">
                    <Fuel className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Reset Fuel</h3>
                    <p className="text-blue-200 text-xs">Add new batch & track history</p>
                  </div>
                </div>
                <button onClick={() => setShowResetFuelModal(false)} className="p-2 hover:bg-white/20 rounded-xl transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Current Fuel Remaining */}
              <div className="px-6 pt-5 flex-shrink-0">
                <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-amber-100 p-2.5 rounded-xl">
                      <Fuel className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Current Fuel Remaining</p>
                      <p className="text-2xl font-bold text-amber-800 font-mono">
                        {fuelRemaining.toLocaleString()} <span className="text-base font-semibold">L</span>
                      </p>
                    </div>
                  </div>
                  <div className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${fuelRemaining <= 0
                      ? 'bg-red-100 text-red-700 border-red-200'
                      : fuelRemaining < 100
                        ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                        : 'bg-green-100 text-green-700 border-green-200'
                    }`}>
                    {fuelRemaining <= 0 ? '⛽ Empty' : fuelRemaining < 100 ? '⚠ Low Stock' : '✓ In Stock'}
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="p-6 border-b border-gray-100 flex-shrink-0">
                <h4 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider">New Batch</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Liters *</label>
                    <input
                      type="number"
                      placeholder="e.g. 500"
                      value={newFuelBatch.liters}
                      onChange={(e) => setNewFuelBatch({ ...newFuelBatch, liters: e.target.value })}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-sm font-semibold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date *</label>
                    <input
                      type="date"
                      value={newFuelBatch.date}
                      onChange={(e) => setNewFuelBatch({ ...newFuelBatch, date: e.target.value })}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Remarks</label>
                    <input
                      type="text"
                      placeholder="Optional..."
                      value={newFuelBatch.remarks}
                      onChange={(e) => setNewFuelBatch({ ...newFuelBatch, remarks: e.target.value })}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={handleResetFuel}
                  disabled={savingBatch || !newFuelBatch.liters}
                  className="mt-4 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {savingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {savingBatch ? 'Saving...' : 'Save Batch'}
                </button>
              </div>

              {/* Batch History */}
              <div className="flex-1 overflow-y-auto p-6">
                <h4 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider">
                  Batch History ({fuelBatchHistory.length})
                </h4>
                {fuelBatchHistory.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Fuel className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No batch history yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">#</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Date</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase">Liters</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Remarks</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Added By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {[...fuelBatchHistory]
                          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                          .map((batch, idx) => (
                            <tr key={batch.id} className="hover:bg-blue-50 transition-colors">
                              <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-800">
                                {formatDate(batch.batchDate)}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg text-xs font-bold">
                                  <Fuel className="w-3 h-3" />
                                  {Number(batch.liters).toLocaleString()} L
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{batch.remarks || '—'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{batch.createdByName || '—'}</td>
                            </tr>
                          ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-blue-50 border-t-2 border-blue-200">
                          <td colSpan={2} className="px-4 py-3 text-sm font-bold text-blue-800">Total</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1 bg-blue-600 text-white px-2.5 py-1 rounded-lg text-xs font-bold">
                              <Fuel className="w-3 h-3" />
                              {fuelBatchHistory.reduce((s, b) => s + (Number(b.liters) || 0), 0).toLocaleString()} L
                            </span>
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}